import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from '../routing';
import api, { UPLOAD_TIMEOUT, CHARGE_TIMEOUT } from '../utils/api';
import { errorText, serverMessage, isIndeterminate } from '../utils/errors';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ArrowLeft, MapPin, Clock, KeyRound, Package, CheckCircle, Star, AlertTriangle, MessageSquare, Camera, X, LifeBuoy, FileText, CalendarPlus, CreditCard, Plus, Minus, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Motion';
import theme from '../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

// The server's vocabulary. Kept in this order because it is roughly the order of
// urgency for someone standing at a locker with a problem.
const ISSUE_CATEGORIES = ['damaged_on_arrival', 'wrong_item', 'locker_problem', 'cannot_return', 'quality', 'other'];
const ISSUE_MAX_CHARS  = 2000;
const ISSUE_MAX_PHOTOS = 4;
const ISSUE_MAX_BYTES  = 5 * 1024 * 1024;
const ISSUE_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// A rental that was never paid for, or was cancelled, has nothing to report
// against — and the endpoint is scoped to the rental's owner anyway.
const CAN_REPORT_STATUSES = ['paid', 'picked_up', 'late', 'returned', 'disputed'];

const issueLabelStyle = { display: 'block', fontSize: 11, color: theme.grey400, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 };

// The receipt is HTML behind a bearer token, so it can never be a plain <a
// href>: a top-level navigation carries no Authorization header and would land
// the customer on a 401 page. The tab has to be opened synchronously inside the
// click — a window.open after an await is treated as an unsolicited popup and
// blocked — and filled in once the markup arrives.
//
// When that window is not there, the old code appended an <a download> on a
// blob: URL and claimed the file had been "saved to your device". Inside the
// Capacitor WebView that does nothing at all — MainActivity is a bare
// BridgeActivity and Capacitor 6 registers no DownloadListener — so the
// customer got a green success toast and no file. The honest fallback is to
// show the document where it can actually be read: inside the app.
const writeIntoWindow = (win, html) => {
  if (win && !win.closed) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    return true;
  }
  return false;
};

// date-fns throws on an invalid date, and a throw inside render takes the whole
// app down with it — there is no route back from a blank screen. Every date here
// comes from the API, where a null column (never picked up, never returned) is
// the ordinary case, so a missing date has to render as a dash rather than crash.
const safeFormat = (value, pattern, locale) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, pattern, { locale });
};

const ISSUE_STATUS_COLORS = {
  open:         theme.state.pending,
  acknowledged: theme.state.info,
  resolved:     theme.state.success,
};

export default function RentalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // One registry decides this, so a third language does not mean finding
  // every page that formats a date. See src/i18n.js.
  const dateLocale = getDateLocale();

  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);

  const [reviewed, setReviewed]         = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewHover, setReviewHover]   = useState(0);
  const [submitingReview, setSubmitingReview] = useState(false);

  // Disputes. The endpoint answers with an array, newest first, and shows a
  // customer only their own — so [] genuinely means "no claim on this rental".
  const [disputes, setDisputes]       = useState([]);
  const [appealText, setAppealText]   = useState('');
  const [appealOpen, setAppealOpen]   = useState(false);
  const [appealing, setAppealing]     = useState(false);
  const [appealClosed, setAppealClosed] = useState(false);

  // Customer-reported problems. Disputes are adminOnly — the customer can only
  // answer a claim already opened against them — so before this the only channel
  // for "it arrived damaged" was the Contact page's mailto: link: no ticket, no
  // record, no photos, and unreliable inside the Capacitor WebView this ships
  // as. Photos here are also how an honest renter establishes that the damage
  // predated them, which is what stops them being charged for it later.
  const [issues, setIssues]             = useState([]);
  const [reportOpen, setReportOpen]     = useState(false);
  const [reportCategory, setReportCategory] = useState('');
  const [reportDesc, setReportDesc]     = useState('');
  const [reportPhotos, setReportPhotos] = useState([]); // [{ file, preview }]
  const [reporting, setReporting]       = useState(false);
  // 0-99 while the photos go up. Without it a 20MB upload on a locker-side
  // connection is indistinguishable from a hung app, which is what made people
  // press the button again.
  const [uploadPct, setUploadPct]       = useState(0);
  const reportingRef = useRef(false);
  const reportInputRef = React.useRef(null);

  // Invoices and credit notes for this rental. They are never emailed while the
  // documents are still specimens, so this screen is the only place a customer
  // can reach one at all.
  const [documents, setDocuments] = useState([]);
  const [openingDoc, setOpeningDoc] = useState(null);
  // The document itself, shown in-app when a new window could not be opened.
  const [docView, setDocView] = useState(null); // { html, title }

  // Extending the rental. Until this existed, the only way to keep the
  // extensions two days longer was to go late and pay €5/day as a penalty —
  // identical money, identical days, relabelled as a fine. The point of this
  // panel is that the customer chooses it, the due date moves, and nobody
  // chases them; the copy has to carry that, not just the price.
  const [extDays, setExtDays]         = useState(1);
  const [quote, setQuote]             = useState(null);
  const [quoteError, setQuoteError]   = useState(false);
  const [quoting, setQuoting]         = useState(false);
  // Bumped to force a re-quote after a successful extension (max_days shrinks)
  // and by the retry button after an outage.
  const [quoteNonce, setQuoteNonce]   = useState(0);
  const [extending, setExtending]     = useState(false);
  const [extendError, setExtendError] = useState(null);
  // Same trap as Checkout's payingRef: `extending` is state, so the disabled
  // prop only lands on the next render and two clicks in one tick both read the
  // stale false. This one charges a card off-session, so it must not double.
  const extendingRef = useRef(false);
  // An attempt whose answer never arrived. The server derives the charge's
  // idempotency key from rental_days + days, and increments rental_days in the
  // same transaction — so a request that COMMITTED but whose response was lost
  // makes any retry with a different day count compute a DIFFERENT key, and
  // Stripe takes the money a second time. While this is set the day picker is
  // frozen, so the only retry possible is byte-for-byte the same request.
  const [extRetryLocked, setExtRetryLocked] = useState(false);
  // The id of the attempt in flight. Minted once per attempt and REUSED by every
  // retry of that attempt, which is what makes the charge idempotent end to end:
  // the server keys Stripe on it, so a retry after a lost response returns the
  // original PaymentIntent instead of taking the money again. Cleared only when
  // an attempt finishes for certain, or when the customer changes the number of
  // days — a different request deserves a different id.
  const extRequestIdRef = useRef(null);
  // The very first quote should not sit behind the debounce — that delay exists
  // for the day picker, not for opening the page.
  const quotedOnceRef = useRef(false);

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [machines, setMachines]           = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [returning, setReturning]         = useState(false);
  const returningRef = useRef(false);
  const [returnResult, setReturnResult]   = useState(null);
  const cancellingRef = useRef(false);
  const [cancelling, setCancelling]       = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = FONTS;
    document.head.appendChild(link);
    api.get(`/rentals/${id}`)
      .then(r => {
        setRental(r);
        if (r.status === 'returned') {
          api.get(`/reviews/check/${r.id}`).then(d => setReviewed(d.reviewed)).catch(() => {});
        }
        // A resolved dispute puts the rental back to 'returned', so 'disputed'
        // alone would hide the outcome the customer most wants to read.
        if (['disputed', 'returned', 'late'].includes(r.status)) {
          api.get(`/rentals/${r.id}/dispute`)
            .then(d => setDisputes(Array.isArray(d) ? d : []))
            .catch(() => {});
        }
        // Load any reports up front: seeing "we have this, it's acknowledged" is
        // what stops the same problem being filed five times.
        if (CAN_REPORT_STATUSES.includes(r.status)) {
          api.get(`/rentals/${r.id}/issues`)
            .then(d => setIssues(Array.isArray(d) ? d : []))
            .catch(() => {});
        }
        // One call returns every document this customer has; the ones belonging
        // to this rental are picked out here. Failing quietly is deliberate — a
        // receipt that does not exist yet is the normal case, and an error about
        // it would only alarm someone whose rental is perfectly fine.
        api.get('/invoices/mine')
          .then(list => setDocuments((Array.isArray(list) ? list : []).filter(d => d.rental_id === r.id)))
          .catch(() => {});
      })
      .catch(() => navigate('/my-rentals'))
      .finally(() => setLoading(false));
    return () => document.head.removeChild(link);
  }, [id]);

  // Only an active rental is a candidate; everything finer (overdue, dispute
  // open, already at the 30-day ceiling) is the quote's `eligible` to decide,
  // because the server is the authority on all of it.
  const canOfferExtension = !!rental && ['paid', 'picked_up'].includes(rental.status);

  // The statutory withdrawal window, mirrored from the server (rentals.js
  // `withdrawalDeadline`): 14 days from the conclusion of the contract, taking
  // whichever of "booked" and "paid" is later. This only decides whether to
  // OFFER the button — the server re-checks it and is the authority, so a clock
  // that is a few minutes out on a phone cannot buy or lose anyone their right.
  const withdrawalDeadline = React.useMemo(() => {
    if (!rental) return null;
    const placed = new Date(rental.created_at).getTime();
    const paid = rental.rental_paid_at ? new Date(rental.rental_paid_at).getTime() : 0;
    const base = Math.max(placed || 0, paid);
    return Number.isFinite(base) && base > 0 ? new Date(base + 14 * 86400000) : null;
  }, [rental]);
  const withdrawalOpen = !!withdrawalDeadline && withdrawalDeadline > new Date();

  useEffect(() => {
    if (!canOfferExtension) return undefined;
    let cancelled = false;
    setQuoting(true);
    // Debounced: the total and the new due date both come from the server, so
    // one request per tap of + would race — the answer for 3 days can land
    // after the answer for 5 and leave the customer looking at the wrong price
    // next to the button that spends their money.
    const timer = setTimeout(() => {
      quotedOnceRef.current = true;
      api.get(`/rentals/${id}/extension-quote`, { params: { days: extDays } })
        .then(q => { if (!cancelled) { setQuote(q); setQuoteError(false); } })
        // No silent failure here: with no quote there is no price and no new
        // date, and a panel that renders nothing looks like the feature does
        // not exist rather than like the network is down.
        .catch(() => { if (!cancelled) setQuoteError(true); })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, quotedOnceRef.current ? 350 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [id, canOfferExtension, extDays, quoteNonce]);

  // The server clamps `days` to max_days when quoting, so leaving the picker
  // above the ceiling would show a total for fewer days than the number on
  // screen — and after an extension the ceiling drops under the picker.
  useEffect(() => {
    const max = quote?.max_days;
    if (Number.isFinite(max) && max > 0 && extDays > max) setExtDays(max);
  }, [quote]); // eslint-disable-line

  // The server writes these messages to be read by the customer, but only in
  // English, and this app is read mostly in Greek. A reason code we know is
  // spoken in their language; anything unrecognised falls back to the server's
  // own wording rather than to a generic failure.
  const refusalMessage = (code, message, maxDays) => {
    if (code === 'MAX_DAYS' && maxDays > 0) {
      return t('rentalDetail.extend.reason.MAX_DAYS_PARTIAL', { count: maxDays });
    }
    const local = code ? t(`rentalDetail.extend.reason.${code}`, { defaultValue: '' }) : '';
    return local || message || t('rentalDetail.extend.failed');
  };

  // After an attempt we never got an answer to, go and look at the rental
  // instead of guessing. Either the extension landed (the days moved) or it did
  // not, and only the server can say which.
  const reconcileExtension = async (daysBefore) => {
    try {
      const fresh = await api.get(`/rentals/${id}`);
      setRental(prev => ({ ...prev, ...fresh }));
      if (Number(fresh.rental_days) > daysBefore) {
        // It committed; only the reply was lost. Nothing to retry, and saying
        // "failed" here is what sent people back to tap the button again.
        setExtendError(null);
        setExtRetryLocked(false);
        extRequestIdRef.current = null;
        setQuoteNonce(n => n + 1);
        toast.success(t('rentalDetail.extend.successToast', {
          date: safeFormat(fresh.due_at, 'd MMMM yyyy', dateLocale),
        }));
        return true;
      }
    } catch (_) {
      // Fall through — an unreachable server tells us nothing either way.
    }
    return false;
  };

  const submitExtension = async () => {
    if (extendingRef.current) return;
    extendingRef.current = true;
    setExtending(true);
    setExtendError(null);
    const daysBefore = Number(rental?.rental_days) || 0;
    if (!extRequestIdRef.current) {
      extRequestIdRef.current =
        (window.crypto?.randomUUID?.() ||
         `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
    }
    try {
      // Longer than the global 15s: this charges a card off-session through
      // Stripe, and giving up at 15s on a request the server was still
      // committing is exactly how the second charge happened.
      const result = await api.post(
        `/rentals/${id}/extend`,
        { days: extDays, request_id: extRequestIdRef.current },
        { timeout: CHARGE_TIMEOUT }
      );
      // Merged, not replaced: the row the server returns is the rentals table
      // on its own, without the product name and locker columns this screen
      // joined in — overwriting would blank the heading mid-page.
      setRental(prev => ({ ...prev, ...result.rental }));
      setExtRetryLocked(false);
      extRequestIdRef.current = null;
      setQuoteNonce(n => n + 1);
      toast.success(t('rentalDetail.extend.successToast', {
        date: safeFormat(result.new_due_at, 'd MMMM yyyy', dateLocale),
      }));
    } catch (err) {
      // A timeout and a refusal are indistinguishable from the client, and the
      // old code treated both as "failed": it cleared the guard, re-enabled the
      // button and left the red axios text under it. On weak signal at a locker
      // that meant a committed €10 charge, a retry, and a second one.
      if (isIndeterminate(err)) {
        const landed = await reconcileExtension(daysBefore);
        if (!landed) {
          // Nothing moved, so most likely nothing was charged — but "most
          // likely" is not good enough for a card. The same request may be sent
          // again (Stripe collapses it onto the same idempotency key); a
          // different number of days may not be, so the picker stays frozen.
          setExtRetryLocked(true);
          setExtendError(t('rentalDetail.extend.unconfirmed'));
          toast.error(t('rentalDetail.extend.unconfirmed'), { duration: 12000 });
        }
        return;
      }
      const msg = refusalMessage(err?.error, serverMessage(err), err?.max_days);
      // Kept in the panel as well as toasted: a declined card or a missing card
      // needs the next step still readable after the toast has gone.
      setExtendError(msg);
      toast.error(msg);
      // A 409 means the rental moved under us (it went late, a dispute opened),
      // so the panel's own eligibility is stale.
      if (err?.status === 409) setQuoteNonce(n => n + 1);
    } finally {
      extendingRef.current = false;
      setExtending(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewRating) return toast.error(t('rentalDetail.reviewSelectStars'));
    setSubmitingReview(true);
    try {
      await api.post('/reviews', { rental_id: id, rating: reviewRating, comment: reviewComment });
      setReviewed(true);
      toast.success(t('rentalDetail.reviewSent'));
    } catch (err) {
      toast.error(errorText(err, t('rentalDetail.reviewFailed')));
    } finally {
      setSubmitingReview(false);
    }
  };

  const submitAppeal = async (e) => {
    e.preventDefault();
    if (!appealText.trim()) return;
    setAppealing(true);
    try {
      const updated = await api.post(`/rentals/${id}/dispute/appeal`, { response: appealText.trim() });
      setDisputes(prev => [updated, ...prev.filter(d => d.id !== updated.id)]);
      setAppealText('');
      setAppealOpen(false);
      toast.success(t('rentalDetail.dispute.appealSent'));
    } catch (err) {
      // The server is the authority on the deadline, not the browser clock. It
      // answers 404 for "too late", "already resolved" and "not yours" alike —
      // for the person actually looking at their own open claim, the only one of
      // those that can be true is that the window shut, so say that plainly
      // instead of leaking a raw server string.
      if (err.status === 404) {
        setAppealClosed(true);
        setAppealOpen(false);
        toast.error(t('rentalDetail.dispute.appealClosedToast'));
        api.get(`/rentals/${id}/dispute`)
          .then(d => setDisputes(Array.isArray(d) ? d : []))
          .catch(() => {});
      } else {
        toast.error(errorText(err, t('rentalDetail.dispute.appealFailed')));
      }
    } finally {
      setAppealing(false);
    }
  };

  const addReportPhotos = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const accepted = [];
    for (const file of incoming) {
      // Rejected here rather than at the server, so the person does not wait for
      // an upload only to be told the file was never going to be accepted.
      if (!ISSUE_PHOTO_TYPES.includes(file.type)) { toast.error(t('rentalDetail.issue.photoBadType')); continue; }
      if (file.size > ISSUE_MAX_BYTES) { toast.error(t('rentalDetail.issue.photoTooLarge', { name: file.name })); continue; }
      accepted.push(file);
    }
    setReportPhotos(prev => {
      const room = ISSUE_MAX_PHOTOS - prev.length;
      if (accepted.length > room) toast.error(t('rentalDetail.issue.photoLimit', { max: ISSUE_MAX_PHOTOS }));
      return [...prev, ...accepted.slice(0, Math.max(0, room)).map(file => ({ file, preview: URL.createObjectURL(file) }))];
    });
  };

  // Object URLs survive until they are revoked or the document is torn down, so
  // walking away from a half-filled report with four previews on screen leaked
  // every one of them. A ref, because the cleanup must run on unmount only and
  // therefore cannot close over the state.
  const reportPhotosRef = useRef([]);
  useEffect(() => { reportPhotosRef.current = reportPhotos; }, [reportPhotos]);
  useEffect(() => () => {
    reportPhotosRef.current.forEach(p => p.preview && URL.revokeObjectURL(p.preview));
  }, []);

  const removeReportPhoto = (index) => {
    setReportPhotos(prev => {
      const target = prev[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const closeReportForm = () => {
    reportPhotos.forEach(p => p.preview && URL.revokeObjectURL(p.preview));
    setReportPhotos([]);
    setReportCategory('');
    setReportDesc('');
    setReportOpen(false);
  };

  const submitReport = async (e) => {
    e.preventDefault();
    if (reportingRef.current) return;
    if (!reportCategory) return toast.error(t('rentalDetail.issue.categoryRequired'));
    if (!reportDesc.trim()) return toast.error(t('rentalDetail.issue.descriptionRequired'));
    reportingRef.current = true;
    setReporting(true);
    setUploadPct(0);
    const countBefore = issues.length;
    try {
      const form = new FormData();
      form.append('category', reportCategory);
      form.append('description', reportDesc.trim());
      // Same field name and multipart convention as the other uploads here.
      reportPhotos.forEach(p => form.append('photos', p.file));

      const created = await api.post(`/rentals/${id}/report-issue`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Four photos is up to 20MB, which does not finish in 15s on the
        // connection someone has standing at a street cabinet. The global
        // timeout aborted it, the form stayed filled, and every retry that HAD
        // landed filed another ticket — the person trying to prove the
        // extensions arrived damaged was the one who could not file it.
        timeout: UPLOAD_TIMEOUT,
        onUploadProgress: (ev) => {
          if (!ev.total) return;
          setUploadPct(Math.min(99, Math.round((ev.loaded / ev.total) * 100)));
        },
      });
      setIssues(prev => [created, ...prev]);
      closeReportForm();
      toast.success(t('rentalDetail.issue.sentToast'));
    } catch (err) {
      // The report endpoint is not idempotent, so a retry after a lost answer
      // files a duplicate. Go and look before offering one.
      if (isIndeterminate(err)) {
        const landed = await reportLanded(countBefore);
        if (landed) {
          closeReportForm();
          toast.success(t('rentalDetail.issue.sentToast'));
          return;
        }
      }
      toast.error(errorText(err, t('rentalDetail.issue.failed')));
    } finally {
      reportingRef.current = false;
      setReporting(false);
      setUploadPct(0);
    }
  };

  // Did the report we lost the answer to actually arrive? Answered by the
  // server's own list rather than by guessing from the error.
  const reportLanded = async (countBefore) => {
    try {
      const list = await api.get(`/rentals/${id}/issues`);
      const fresh = Array.isArray(list) ? list : [];
      if (fresh.length > countBefore) {
        setIssues(fresh);
        return true;
      }
    } catch (_) {
      // Unreachable server — say nothing rather than claim either outcome.
    }
    return false;
  };

  const openDocument = async (doc) => {
    // Opened before the await, for the popup-blocker reason explained above.
    const win = window.open('', '_blank');
    setOpeningDoc(doc.id);
    try {
      const html = await api.get(`/invoices/${doc.id}`, { responseType: 'text' });
      const markup = typeof html === 'string' ? html : String(html);
      // No window (the Android WebView, or a blocked popup): render it here
      // instead. Claiming it had been downloaded was a lie the customer could
      // only discover by going to look for a file that was never written.
      if (!writeIntoWindow(win, markup)) setDocView({ html: markup, title: doc.document_number });
    } catch (err) {
      if (win && !win.closed) win.close();
      toast.error(errorText(err, t('rentalDetail.document.openFailed')));
    } finally {
      setOpeningDoc(null);
    }
  };

  const openReturnModal = async () => {
    setShowReturnModal(true);
    setLoadingMachines(true);
    try {
      const data = await api.get('/locker/machines');
      setMachines(data);
    } catch {
      toast.error(t('rentalDetail.loadMachinesFailed'));
    } finally {
      setLoadingMachines(false);
    }
  };

  // The machine's UUID, not its Bestwond device number: the device number is no
  // longer published by the public machines list, and the backend resolves
  // either form.
  const handleReturn = async (unit_id) => {
    // `returning` is state, so the disabled prop only lands on the next render
    // and two taps in one tick both read the stale false. Two return orders mean
    // two save codes, and the customer standing at the cabinet is holding the
    // one the machine no longer expects.
    if (returningRef.current) return;
    returningRef.current = true;
    setReturning(true);
    try {
      const result = await api.post('/locker/return/initiate', { rental_id: id, unit_id });
      setReturnResult(result);
      const updated = await api.get(`/rentals/${id}`);
      setRental(prev => ({ ...prev, ...updated }));
    } catch (err) {
      toast.error(errorText(err, t('rentalDetail.returnFailed')));
    } finally {
      returningRef.current = false;
      setReturning(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 120, fontFamily: theme.font.display, fontSize: 24, color: theme.gold }}>...</div>;
  if (!rental) return null;

  const statusFlow = ['pending_payment', 'paid', 'picked_up', 'returned'];
  // 'late' is not a step of its own — the item is out, which is exactly the
  // 'picked_up' position. indexOf returned -1 for it, greying out every circle,
  // so an overdue customer holding the extensions and accruing fees saw a
  // tracker claiming they had never even booked, right above the red late panel.
  // 'disputed' and 'cancelled' are not points on this line at all; they get
  // their own panel below instead of a misleading one.
  const STATUS_STEP_ALIAS = { late: 'picked_up' };
  // 'lost' joins these: a set written off as never returned has no position on a
  // line that ends in "returned", and showing it stuck on step 3 would suggest we
  // are still waiting for something.
  const haltedStatus = ['disputed', 'cancelled', 'lost'].includes(rental.status) ? rental.status : null;
  const HALTED_STYLE = {
    disputed:  { bg: theme.purpleWash, border: theme.purpleBorder, icon: theme.purple },
    cancelled: { bg: theme.grey100, border: theme.grey120, icon: theme.grey450 },
    lost:      { bg: theme.grey100, border: theme.grey120, icon: theme.ink },
  };
  const haltedStyle = HALTED_STYLE[haltedStatus] || HALTED_STYLE.cancelled;
  const HALTED_KEY = { disputed: 'Disputed', cancelled: 'Cancelled', lost: 'Lost' };
  const currentIdx = statusFlow.indexOf(STATUS_STEP_ALIAS[rental.status] || rental.status);
  const statusLabels = [
    t('rentalDetail.statusBooked'),
    t('rentalDetail.statusPaid'),
    t('rentalDetail.statusCollected'),
    t('rentalDetail.statusReturned'),
  ];

  const canReport = CAN_REPORT_STATUSES.includes(rental.status);
  const charsLeft = ISSUE_MAX_CHARS - reportDesc.length;

  const timelineRows = [
    [t('rentalDetail.timelineBooked'),  rental.created_at],
    [t('rentalDetail.timelinePayment'), rental.rental_paid_at],
    [t('rentalDetail.timelineStarts'),  rental.starts_at],
    [t('rentalDetail.timelineDue'),     rental.due_at],
    [t('rentalDetail.timelinePickedUp'),rental.picked_up_at],
    [t('rentalDetail.timelineReturned'),rental.returned_at],
  ];

  // ── Dispute state ───────────────────────────────────────────
  const dispute         = disputes[0] || null;
  const disputeLive     = !!dispute && ['open', 'appealed'].includes(dispute.status);
  const disputeResolved = !!dispute && ['upheld', 'waived'].includes(dispute.status);
  const depositAmount   = parseFloat(rental.deposit_amount) || 0;
  const claimAmount     = dispute ? parseFloat(dispute.amount_claimed) || 0 : 0;
  // The client clock only decides what to *render*; the server decides what is
  // accepted. Both have to agree that a shut window shows no form.
  const deadlinePassed  = !!dispute && new Date(dispute.appeal_deadline) <= new Date();
  const canAppeal       = disputeLive && !deadlinePassed && !appealClosed;
  const showAppealForm  = canAppeal && (!dispute.customer_response || appealOpen);
  // amount_final is what was DECIDED; amount_claimed is what was asked for at the
  // start. Showing the claim meant a customer who won a partial appeal — €80
  // claimed, €30 upheld — read "Kept €80 / Returned €140" on the exact screen
  // Terms §7 name as the record, while the refund endpoint deducted €30 and paid
  // back €190. We told her €50 more had been taken than really was.
  const settledAmount   = dispute && Number.isFinite(parseFloat(dispute.amount_final))
    ? parseFloat(dispute.amount_final)
    : claimAmount;
  const deductedAmount  = disputeResolved && dispute.status === 'upheld' ? settledAmount : 0;
  const refundedAmount  = rental.deposit_refunded_at
    ? parseFloat(rental.deposit_refund_amount ?? depositAmount)
    : Math.max(0, depositAmount - deductedAmount);
  const disputeAccent   = disputeLive ? theme.amber : dispute?.status === 'upheld' ? theme.red : theme.green;

  const disputeRow = (label, value, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '9px 0', borderBottom: `1px solid ${theme.creamBone}`, fontSize: 14 }}>
      <span style={{ color: theme.grey400, flexShrink: 0 }}>{label}</span>
      <span style={{ color: color || theme.ink, textAlign: 'right' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.cream, fontFamily: theme.font.body, paddingBottom: 80 }}>
      <PageHero
        title={rental.product_name}
        subtitle={t('rentalDetail.rentalId', { id: rental.id.slice(0, 8).toUpperCase() })}
        height={250}
      />
      {/* One entrance for the whole record. The blocks below carry the pickup
          code, the deadline and the deposit state — none of them should wait
          on an animation to become readable. */}
      <Reveal style={{ maxWidth: 700, margin: '0 auto', padding: '40px 24px 0' }}>
        <Link to="/my-rentals" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: theme.grey400, textDecoration: 'none', fontSize: 13, marginBottom: 32 }}>
          <ArrowLeft size={16} /> {t('rentalDetail.backLink')}
        </Link>

        {/* Status timeline */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
          <h3 style={{ fontFamily: theme.font.display, fontSize: 20, marginBottom: 24 }}>{t('rentalDetail.statusTitle')}</h3>
          {haltedStatus ? (
            <div style={{
              display: 'flex', gap: 14, alignItems: 'flex-start',
              background: haltedStyle.bg,
              border: `1px solid ${haltedStyle.border}`,
              borderRadius: 4, padding: 18,
            }}>
              <AlertTriangle size={18} color={haltedStyle.icon} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontFamily: theme.font.display, fontSize: 19, color: theme.ink, marginBottom: 6 }}>
                  {t(`rentalDetail.status${HALTED_KEY[haltedStatus]}Title`)}
                </div>
                <p style={{ color: theme.grey600, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                  {t(`rentalDetail.status${HALTED_KEY[haltedStatus]}Body`)}
                </p>
                {/* The one number she will look for. Stated here rather than
                    only in the email, because the email is a month old by the
                    time anyone goes looking for it. */}
                {haltedStatus === 'lost' && rental.deposit_forfeited_amount != null && (
                  <p style={{ color: theme.ink, fontSize: 13, lineHeight: 1.7, margin: '10px 0 0' }}>
                    {t('rentalDetail.statusLostAmounts', {
                      kept: (parseFloat(rental.deposit_forfeited_amount) || 0).toFixed(2),
                      fees: (parseFloat(rental.late_fee_charged) || 0).toFixed(2),
                    })}
                  </p>
                )}
              </div>
            </div>
          ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {statusLabels.map((label, i) => {
              const done = currentIdx > i;
              const active = currentIdx === i;
              return (
                <React.Fragment key={i}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: done ? theme.gold : active ? theme.ink : theme.sand, color: done || active ? theme.white : theme.grey400, fontSize: 13 }}>
                      {done ? '✓' : i + 1}
                    </div>
                    <div style={{ fontSize: 12, color: active ? theme.ink : done ? theme.gold : theme.grey400, whiteSpace: 'nowrap' }}>{label}</div>
                  </div>
                  {i < 3 && <div style={{ flex: 1, height: 2, background: done ? theme.gold : theme.sand, margin: '0 8px', marginBottom: 20 }} />}
                </React.Fragment>
              );
            })}
          </div>
          )}
          {rental.status === 'late' && (
            <p style={{ color: theme.red, fontSize: 13, lineHeight: 1.7, margin: '18px 0 0' }}>
              {t('rentalDetail.statusLateNote')}
            </p>
          )}
        </div>

        {/* Dispute panel — why the deposit has not come back, and how to answer */}
        {dispute && (
          <div style={{ background: theme.white, border: `2px solid ${disputeAccent}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              {disputeResolved ? <CheckCircle size={20} color={disputeAccent} /> : <AlertTriangle size={20} color={disputeAccent} />}
              <h3 style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, margin: 0 }}>
                {disputeLive
                  ? t('rentalDetail.dispute.title')
                  : dispute.status === 'upheld'
                    ? t('rentalDetail.dispute.upheldTitle')
                    : t('rentalDetail.dispute.waivedTitle')}
              </h3>
            </div>

            <p style={{ color: theme.grey600, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
              {disputeLive
                ? t('rentalDetail.dispute.intro')
                : dispute.status === 'upheld'
                  ? t('rentalDetail.dispute.upheldIntro')
                  : t('rentalDetail.dispute.waivedIntro')}
            </p>

            {disputeRow(t('rentalDetail.dispute.reasonLabel'), dispute.reason)}
            {disputeRow(
              disputeResolved ? t('rentalDetail.dispute.originallyClaimed') : t('rentalDetail.dispute.amountLabel'),
              `€${claimAmount.toFixed(2)}`,
              disputeLive ? theme.red : undefined
            )}
            {disputeRow(t('rentalDetail.dispute.depositLabel'), `€${depositAmount.toFixed(2)}`)}
            {disputeLive && disputeRow(
              t('rentalDetail.dispute.deadlineLabel'),
              safeFormat(dispute.appeal_deadline, 'd MMMM yyyy, HH:mm', dateLocale),
              deadlinePassed ? theme.grey400 : theme.ink
            )}

            {disputeResolved && (
              <>
                {disputeRow(t('rentalDetail.dispute.deductedLabel'), `€${deductedAmount.toFixed(2)}`, deductedAmount > 0 ? theme.red : theme.green)}
                {disputeRow(t('rentalDetail.dispute.refundedLabel'), `€${refundedAmount.toFixed(2)}`, theme.green)}
                {dispute.resolution_notes && disputeRow(t('rentalDetail.dispute.notesLabel'), dispute.resolution_notes)}
              </>
            )}

            {/* What the customer already told us */}
            {dispute.customer_response && (
              <div style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 18, marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <MessageSquare size={15} color={theme.gold} />
                  <span style={{ fontSize: 12, color: theme.grey500, letterSpacing: '0.08em' }}>{t('rentalDetail.dispute.yourResponseTitle')}</span>
                </div>
                <p style={{ color: theme.ink, fontSize: 14, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{dispute.customer_response}</p>
                {dispute.customer_responded_at && (
                  <div style={{ fontSize: 12, color: theme.grey400, marginTop: 10 }}>
                    {t('rentalDetail.dispute.respondedAt', {
                      date: safeFormat(dispute.customer_responded_at, 'd MMM yyyy, HH:mm', dateLocale),
                    })}
                  </div>
                )}
                {disputeLive && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, color: theme.amber, fontSize: 13 }}>
                    <Clock size={14} /> {t('rentalDetail.dispute.awaitingReview')}
                  </div>
                )}
              </div>
            )}

            {/* Appeal form — only while the window is genuinely open */}
            {showAppealForm && (
              <form onSubmit={submitAppeal} style={{ marginTop: 20 }}>
                <h4 style={{ fontFamily: theme.font.display, fontSize: 19, color: theme.ink, margin: '0 0 8px' }}>
                  {dispute.customer_response ? t('rentalDetail.dispute.addMoreTitle') : t('rentalDetail.dispute.appealTitle')}
                </h4>
                <p style={{ color: theme.grey500, fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>{t('rentalDetail.dispute.appealHelp')}</p>
                <textarea
                  value={appealText}
                  onChange={e => setAppealText(e.target.value)}
                  placeholder={t('rentalDetail.dispute.appealPlaceholder')}
                  rows={5}
                  style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 2, fontSize: 14, fontFamily: theme.font.body, resize: 'vertical', boxSizing: 'border-box', outline: 'none', background: theme.cream }}
                />
                <button type="submit" disabled={appealing || !appealText.trim()}
                  style={{ marginTop: 16, padding: '13px 34px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: appealText.trim() && !appealing ? 'pointer' : 'not-allowed', opacity: appealText.trim() && !appealing ? 1 : 0.5, fontFamily: theme.font.body }}>
                  {appealing ? t('rentalDetail.dispute.appealSending') : t('rentalDetail.dispute.appealSubmit')}
                </button>
              </form>
            )}

            {/* Already answered, window still open — let them add to it */}
            {canAppeal && dispute.customer_response && !appealOpen && (
              <button onClick={() => setAppealOpen(true)}
                style={{ marginTop: 16, background: 'none', border: `1px solid ${theme.sand}`, color: theme.grey600, padding: '10px 24px', borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer', fontFamily: theme.font.body }}>
                {t('rentalDetail.dispute.addMoreBtn')}
              </button>
            )}

            {/* Window shut — say so instead of leaving a dead form */}
            {disputeLive && !canAppeal && (
              <div style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 18, marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: theme.grey500, fontSize: 12, letterSpacing: '0.08em' }}>
                  <Clock size={14} /> {t('rentalDetail.dispute.windowClosedTitle')}
                </div>
                <p style={{ color: theme.grey600, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                  {t('rentalDetail.dispute.windowClosedBody')}
                </p>
              </div>
            )}

            {/* Resolving a dispute moves no money — an admin still has to press
                refund. "The refund is on its way" was a statement about a
                transfer that had not been made, and the figure beside it ignored
                any uncollected late fee that will come off the same deposit. */}
            {disputeResolved && !rental.deposit_refunded_at && (
              <p style={{ color: theme.grey500, fontSize: 13, lineHeight: 1.7, marginTop: 20, marginBottom: 0 }}>
                {t('rentalDetail.dispute.refundToCome')}
              </p>
            )}
            {disputeResolved && rental.deposit_refunded_at && (
              <p style={{ color: theme.grey500, fontSize: 13, lineHeight: 1.7, marginTop: 20, marginBottom: 0 }}>
                {t('rentalDetail.dispute.refundPending')}
              </p>
            )}
          </div>
        )}

        {/* Pickup code (6-digit — the Bestwond cabinet has no QR scanner) */}
        {rental.pickup_take_code && ['paid', 'picked_up'].includes(rental.status) && (
          <div style={{ background: theme.white, border: `2px solid ${theme.gold}`, borderRadius: 4, padding: 32, marginBottom: 24, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
              <KeyRound size={20} color={theme.gold} />
              <h3 style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, margin: 0 }}>{t('rentalDetail.pickupCodeTitle')}</h3>
            </div>
            <div style={{ fontSize: 44, letterSpacing: '0.25em', fontWeight: 700, color: theme.ink }}>{rental.pickup_take_code}</div>
            {rental.pickup_box_name && (
              <div style={{ fontSize: 13, color: theme.grey500, marginTop: 8 }}>{t('rentalDetail.boxLabel')}: <strong>{rental.pickup_box_name}</strong></div>
            )}
            <p style={{ color: theme.grey500, fontSize: 13, marginTop: 16 }}>{t('rentalDetail.pickupCodeInstructions')}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', marginTop: 12, color: theme.grey600, fontSize: 13 }}>
              <MapPin size={14} color={theme.gold} />
              {rental.locker_location || 'GlamGo Station'}
            </div>
          </div>
        )}

        {/* Extend — the honest version of "keep them two more days". Priced the
            same as the late fee on purpose; what changes is that the due date
            moves and no one comes after the customer for the item. */}
        {canOfferExtension && (
          <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <CalendarPlus size={18} color={theme.gold} />
              <h3 style={{ fontFamily: theme.font.display, fontSize: 20, margin: 0 }}>{t('rentalDetail.extend.title')}</h3>
            </div>

            {quoteError ? (
              <>
                <p style={{ color: theme.grey600, fontSize: 13, lineHeight: 1.7, margin: '0 0 14px' }}>{t('rentalDetail.extend.quoteFailed')}</p>
                <button onClick={() => setQuoteNonce(n => n + 1)} disabled={quoting}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: `1px solid ${theme.gold}`, color: theme.goldInk, padding: '11px 24px', borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', cursor: quoting ? 'wait' : 'pointer', fontFamily: theme.font.body }}>
                  <RefreshCw size={13} /> {t('rentalDetail.extend.retry')}
                </button>
              </>
            ) : !quote ? (
              <p style={{ color: theme.grey400, fontSize: 13, margin: 0 }}>{t('rentalDetail.extend.quoteLoading')}</p>
            ) : !quote.eligible || !(quote.max_days > 0) ? (
              // Deliberately no picker and no button here. Someone who is
              // overdue needs to read "bring it back", not to be offered a
              // purchase that the server is going to refuse anyway.
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 18 }}>
                <AlertTriangle size={16} color={quote.reason === 'ALREADY_LATE' ? theme.red : theme.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                <p style={{ color: quote.reason === 'ALREADY_LATE' ? theme.red : theme.grey600, fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                  {refusalMessage(quote.reason || 'MAX_DAYS', quote.message, 0)}
                </p>
              </div>
            ) : (
              <>
                <p style={{ color: theme.grey600, fontSize: 13, lineHeight: 1.7, margin: '0 0 20px' }}>
                  {t('rentalDetail.extend.subtitle', { perDay: parseFloat(quote.per_day || 0).toFixed(2) })}
                </p>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 14, color: theme.ink }}>{t('rentalDetail.extend.daysLabel')}</div>
                    <div style={{ color: theme.grey400, fontSize: 12, marginTop: 2 }}>{t('rentalDetail.extend.maxDaysNote', { count: quote.max_days })}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button onClick={() => setExtDays(d => Math.max(1, d - 1))} disabled={extDays <= 1 || extending || extRetryLocked}
                      aria-label={t('rentalDetail.extend.fewerDays')}
                      style={{ width: 36, height: 36, border: `1px solid ${theme.sand}`, borderRadius: '50%', background: 'none', cursor: extDays <= 1 || extending || extRetryLocked ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: extDays <= 1 || extRetryLocked ? 0.4 : 1 }}>
                      <Minus size={16} />
                    </button>
                    <span style={{ fontFamily: theme.font.display, fontSize: 28, color: theme.ink, minWidth: 32, textAlign: 'center' }}>{extDays}</span>
                    <button onClick={() => setExtDays(d => Math.min(quote.max_days, d + 1))} disabled={extDays >= quote.max_days || extending || extRetryLocked}
                      aria-label={t('rentalDetail.extend.moreDays')}
                      style={{ width: 36, height: 36, border: `1px solid ${theme.sand}`, borderRadius: '50%', background: 'none', cursor: extDays >= quote.max_days || extending || extRetryLocked ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: extDays >= quote.max_days || extRetryLocked ? 0.4 : 1 }}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* The new date is the thing being bought — it is given more
                    weight than the amount, and dimmed rather than swapped for a
                    spinner while a fresh quote is in flight, so the number under
                    the button is never a confident-looking stale one. */}
                <div style={{ background: theme.cream, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 18, marginBottom: 18, opacity: quoting ? 0.45 : 1, transition: 'opacity 0.15s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13, color: theme.grey400, marginBottom: 10 }}>
                    <span>{t('rentalDetail.extend.currentDueLabel')}</span>
                    <span style={{ color: theme.grey600, textAlign: 'right' }}>
                      {safeFormat(quote.current_due_at, 'd MMMM yyyy, HH:mm', dateLocale)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'baseline', marginBottom: 12 }}>
                    <span style={{ fontSize: 13, color: theme.grey400 }}>{t('rentalDetail.extend.newDueLabel')}</span>
                    <span style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink, textAlign: 'right' }}>
                      {safeFormat(quote.new_due_at, 'd MMMM yyyy, HH:mm', dateLocale)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, borderTop: `1px solid ${theme.sand}`, paddingTop: 12, fontSize: 14 }}>
                    <span style={{ color: theme.grey400 }}>{t('rentalDetail.extend.totalLabel')}</span>
                    <span style={{ color: theme.ink }}>€{parseFloat(quote.total ?? 0).toFixed(2)}</span>
                  </div>
                </div>

                {/* Said before the button, not after: a charge that happens with
                    no card form in sight is alarming unless you were told. */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 18 }}>
                  <CreditCard size={15} color={theme.gold} style={{ flexShrink: 0, marginTop: 2 }} />
                  <p style={{ color: theme.grey500, fontSize: 12, lineHeight: 1.7, margin: 0 }}>{t('rentalDetail.extend.cardNote')}</p>
                </div>

                <button onClick={submitExtension} disabled={extending || quoting}
                  style={{ width: '100%', padding: '16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: extending || quoting ? 'wait' : 'pointer', opacity: extending || quoting ? 0.6 : 1, fontFamily: theme.font.body, textTransform: 'uppercase' }}>
                  {extending
                    ? t('rentalDetail.extend.submitting')
                    : extRetryLocked
                      // Deliberately the SAME request: same days, same amount,
                      // so Stripe collapses it onto the same idempotency key if
                      // the first one did get through after all.
                      ? t('rentalDetail.extend.retrySame')
                      : t('rentalDetail.extend.submit', { count: extDays, total: parseFloat(quote.total ?? 0).toFixed(2) })}
                </button>

                {extendError && (
                  <p style={{ color: theme.red, fontSize: 13, lineHeight: 1.7, margin: '14px 0 0' }}>{extendError}</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Return button */}
        {['picked_up', 'late'].includes(rental.status) && (
          <div style={{ background: theme.white, border: `2px solid ${rental.status === 'late' ? theme.red : theme.ink}`, borderRadius: 4, padding: 28, marginBottom: 24, textAlign: 'center' }}>
            <Package size={32} color={rental.status === 'late' ? theme.red : theme.gold} style={{ marginBottom: 16 }} />
            <h3 style={{ fontFamily: theme.font.display, fontSize: 24, marginBottom: 8 }}>
              {rental.status === 'late' ? t('rentalDetail.returnLate') : t('rentalDetail.returnReady')}
            </h3>
            {rental.status === 'late' && (
              <p style={{ color: theme.red, fontSize: 13, marginBottom: 12, fontWeight: 500 }}>
                {t('rentalDetail.returnWarning')}
              </p>
            )}
            <p style={{ color: theme.grey500, fontSize: 14, marginBottom: 24 }}>
              {t('rentalDetail.returnInstructions')}
            </p>
            <button onClick={openReturnModal} style={{ padding: '16px 40px', background: rental.status === 'late' ? theme.red : theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.1em', cursor: 'pointer', fontFamily: theme.font.body }}>
              {t('rentalDetail.returnHere')}
            </button>
          </div>
        )}

        {/* Report a problem — the customer's only channel into the system.
            Disputes are adminOnly, so without this the answer to "it arrived
            damaged" was a mailto: link that often does nothing on Android. */}
        {canReport && (
          <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <LifeBuoy size={18} color={theme.gold} />
              <h3 style={{ fontFamily: theme.font.display, fontSize: 20, margin: 0 }}>{t('rentalDetail.issue.title')}</h3>
            </div>
            <p style={{ color: theme.grey500, fontSize: 13, lineHeight: 1.7, margin: '0 0 20px' }}>{t('rentalDetail.issue.subtitle')}</p>

            {/* Existing reports first, so the answer to "did it go through?" is
                visible before the form invites a second identical one. */}
            {issues.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                {issues.map(iss => {
                  const sc = ISSUE_STATUS_COLORS[iss.status] || { bg: theme.grey100, color: theme.grey600 };
                  return (
                    <div key={iss.id} style={{ border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '14px 16px', background: theme.cream }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 14, color: theme.ink, fontWeight: 500 }}>
                          {t(`rentalDetail.issue.category.${iss.category}`, { defaultValue: iss.category })}
                        </span>
                        <span style={{ background: sc.bg, color: sc.color, fontSize: 11, padding: '3px 10px', borderRadius: 100, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                          {t(`rentalDetail.issue.status.${iss.status}`, { defaultValue: iss.status })}
                        </span>
                      </div>
                      <p style={{ color: theme.grey600, fontSize: 13, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' }}>{iss.description}</p>
                      {iss.photo_urls?.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                          {iss.photo_urls.map((url, i) => (
                            <img key={i} src={url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                          ))}
                        </div>
                      )}
                      {iss.created_at && (
                        <div style={{ color: theme.grey400, fontSize: 12, marginTop: 10 }}>
                          {t('rentalDetail.issue.reportedAt', { date: safeFormat(iss.created_at, 'd MMM yyyy, HH:mm', dateLocale) })}
                        </div>
                      )}
                      {iss.resolution_notes && (
                        <p style={{ color: theme.greenDeep, fontSize: 13, lineHeight: 1.7, margin: '10px 0 0' }}>{iss.resolution_notes}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!reportOpen ? (
              <button onClick={() => setReportOpen(true)}
                style={{ background: 'none', border: `1px solid ${theme.gold}`, color: theme.goldInk, padding: '12px 28px', borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer', fontFamily: theme.font.body }}>
                {issues.length ? t('rentalDetail.issue.reportAnotherBtn') : t('rentalDetail.issue.reportBtn')}
              </button>
            ) : (
              <form onSubmit={submitReport}>
                <label style={issueLabelStyle}>{t('rentalDetail.issue.categoryLabel')}</label>
                {/* Readable choices, not enum names — this is read by someone
                    standing at a locker with a problem. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                  {ISSUE_CATEGORIES.map(c => (
                    <button key={c} type="button" onClick={() => setReportCategory(c)}
                      aria-pressed={reportCategory === c}
                      style={{
                        padding: '9px 16px', borderRadius: 100, cursor: 'pointer', fontFamily: theme.font.body,
                        fontSize: 12, letterSpacing: '0.03em', textAlign: 'left',
                        border: reportCategory === c ? 'none' : `1px solid ${theme.sandMid}`,
                        background: reportCategory === c ? theme.ink : 'transparent',
                        color: reportCategory === c ? theme.white : theme.grey600,
                      }}>
                      {t(`rentalDetail.issue.category.${c}`)}
                    </button>
                  ))}
                </div>

                <label style={issueLabelStyle}>{t('rentalDetail.issue.descriptionLabel')}</label>
                <textarea
                  value={reportDesc}
                  onChange={e => setReportDesc(e.target.value.slice(0, ISSUE_MAX_CHARS))}
                  // maxLength alone would not stop a paste in every browser, and
                  // the server rejects anything over 2000 outright.
                  maxLength={ISSUE_MAX_CHARS}
                  rows={5}
                  placeholder={t('rentalDetail.issue.descriptionPlaceholder')}
                  style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 2, fontSize: 14, fontFamily: theme.font.body, resize: 'vertical', boxSizing: 'border-box', outline: 'none', background: theme.cream }}
                />
                <div style={{ fontSize: 11, color: charsLeft < 100 ? theme.amber : theme.grey400, marginTop: 6, marginBottom: 20, textAlign: 'right' }}>
                  {t('rentalDetail.issue.charsLeft', { count: charsLeft })}
                </div>

                <label style={issueLabelStyle}>{t('rentalDetail.issue.photosLabel')}</label>
                <p style={{ color: theme.grey400, fontSize: 12, lineHeight: 1.6, margin: '0 0 10px' }}>
                  {t('rentalDetail.issue.photosHint', { max: ISSUE_MAX_PHOTOS })}
                </p>
                {/* Optional, but for damage on arrival a photo is the whole
                    protection against being charged for the previous renter. */}
                {reportCategory === 'damaged_on_arrival' && reportPhotos.length === 0 && (
                  <p style={{ background: theme.creamDeep, border: `1px solid ${theme.sandDeep}`, borderRadius: 4, padding: 12, color: theme.goldMoss, fontSize: 12, lineHeight: 1.7, margin: '0 0 12px' }}>
                    {t('rentalDetail.issue.photosNudge')}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
                  {reportPhotos.map((p, i) => (
                    <div key={p.preview} style={{ position: 'relative' }}>
                      <img src={p.preview} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                      <button type="button" onClick={() => removeReportPhoto(i)}
                        aria-label={t('rentalDetail.issue.removePhoto')}
                        style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: theme.red, border: `2px solid ${theme.white}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        <X size={11} color={theme.white} />
                      </button>
                    </div>
                  ))}
                  {reportPhotos.length < ISSUE_MAX_PHOTOS && (
                    <button type="button" onClick={() => reportInputRef.current?.click()}
                      style={{ width: 64, height: 64, borderRadius: 4, background: theme.cream, border: `1px dashed ${theme.gold}`, color: theme.gold, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      aria-label={t('rentalDetail.issue.addPhoto')}>
                      <Camera size={18} />
                    </button>
                  )}
                  {/* No capture attribute: the photo people want is usually one
                      already in the gallery, not a fresh rear-camera shot. */}
                  <input
                    ref={reportInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => { addReportPhotos(e.target.files); e.target.value = ''; }}
                  />
                </div>

                {/* Photos take a while on the connection someone has at a
                    cabinet. Without a visible bar the wait reads as a hang, and
                    a hang is what makes people submit the same report twice. */}
                {reporting && uploadPct > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ height: 4, background: theme.creamBone, borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${uploadPct}%`, background: theme.gold, transition: 'width 0.2s' }} />
                    </div>
                    <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6 }}>
                      {t('rentalDetail.issue.uploading', { percent: uploadPct })}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button type="submit" disabled={reporting || !reportCategory || !reportDesc.trim()}
                    style={{ padding: '13px 34px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: reporting || !reportCategory || !reportDesc.trim() ? 'not-allowed' : 'pointer', opacity: reporting || !reportCategory || !reportDesc.trim() ? 0.5 : 1, fontFamily: theme.font.body }}>
                    {reporting ? t('rentalDetail.issue.submitting') : t('rentalDetail.issue.submit')}
                  </button>
                  <button type="button" onClick={closeReportForm} disabled={reporting}
                    style={{ padding: '13px 24px', background: 'none', border: `1px solid ${theme.sand}`, color: theme.grey600, borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer', fontFamily: theme.font.body }}>
                    {t('rentalDetail.issue.cancelBtn')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Payment details */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
          <h3 style={{ fontFamily: theme.font.display, fontSize: 20, marginBottom: 20 }}>{t('rentalDetail.paymentSummary')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: theme.grey600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('rentalDetail.rental')} ({rental.rental_days} {t('rentalDetail.days')})</span>
              <span>€{parseFloat(rental.total_rental_price).toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{t('rentalDetail.deposit')}</span>
              <span>€{parseFloat(rental.deposit_amount).toFixed(2)}</span>
            </div>
            {rental.deposit_refunded_at && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.green }}>
                <span>{t('rentalDetail.depositRefunded')}</span>
                <span>€{parseFloat(rental.deposit_refund_amount || rental.deposit_amount).toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Receipt / credit note. Rendered only when one actually exists — an
            always-visible button would be a dead link for every rental that has
            not been invoiced yet, which is most of them at any moment. */}
        {documents.length > 0 && (
          <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <FileText size={18} color={theme.gold} />
              <h3 style={{ fontFamily: theme.font.display, fontSize: 20, margin: 0 }}>{t('rentalDetail.document.title')}</h3>
            </div>
            <p style={{ color: theme.grey500, fontSize: 13, lineHeight: 1.7, margin: '0 0 18px' }}>{t('rentalDetail.document.subtitle')}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {documents.map(doc => (
                <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '14px 16px', background: theme.cream }}>
                  <div>
                    <div style={{ color: theme.ink, fontSize: 14 }}>
                      {doc.doc_type === 'credit_note' ? t('rentalDetail.document.creditNote') : t('rentalDetail.document.invoice')}
                      <span style={{ fontFamily: theme.font.mono, color: theme.grey500, fontSize: 12, marginLeft: 8 }}>{doc.document_number}</span>
                    </div>
                    <div style={{ color: theme.grey400, fontSize: 12, marginTop: 4 }}>
                      {safeFormat(doc.issued_at, 'd MMM yyyy', dateLocale)}
                      {' · '}€{(parseFloat(doc.total_gross) || 0).toFixed(2)}
                    </div>
                  </div>
                  <button onClick={() => openDocument(doc)} disabled={openingDoc === doc.id}
                    style={{ padding: '11px 24px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', cursor: openingDoc === doc.id ? 'wait' : 'pointer', opacity: openingDoc === doc.id ? 0.6 : 1, fontFamily: theme.font.body }}>
                    {openingDoc === doc.id ? t('rentalDetail.document.opening') : t('rentalDetail.document.view')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: 28 }}>
          <h3 style={{ fontFamily: theme.font.display, fontSize: 20, marginBottom: 20 }}>{t('rentalDetail.timeline')}</h3>
          {timelineRows.filter(([, d]) => d).map(([label, date]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${theme.creamBone}`, fontSize: 14 }}>
              <span style={{ color: theme.grey400 }}>{label}</span>
              <span style={{ color: theme.ink }}>{safeFormat(date, 'MMM d, yyyy — HH:mm', dateLocale)}</span>
            </div>
          ))}
        </div>

        {/* Review form */}
        {rental.status === 'returned' && (
          <div style={{ background: theme.white, border: reviewed ? `1px solid ${theme.greenWash}` : `2px solid ${theme.gold}`, borderRadius: 4, padding: 28, marginTop: 24 }}>
            {reviewed ? (
              <div style={{ textAlign: 'center', color: theme.green }}>
                <CheckCircle size={28} style={{ marginBottom: 8 }} />
                <div style={{ fontFamily: theme.font.display, fontSize: 20 }}>{t('rentalDetail.reviewThanks')}</div>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: theme.font.display, fontSize: 22, marginBottom: 20 }}>{t('rentalDetail.reviewTitle')}</h3>
                <form onSubmit={submitReview}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
                    {[1,2,3,4,5].map(i => (
                      <button key={i} type="button"
                        onMouseEnter={() => setReviewHover(i)}
                        onMouseLeave={() => setReviewHover(0)}
                        onClick={() => setReviewRating(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                        <Star size={28} fill={(reviewHover || reviewRating) >= i ? theme.gold : 'none'} color={theme.gold} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={reviewComment}
                    onChange={e => setReviewComment(e.target.value)}
                    placeholder={t('rentalDetail.reviewPlaceholder')}
                    rows={3}
                    style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 2, fontSize: 14, fontFamily: theme.font.body, resize: 'vertical', boxSizing: 'border-box', outline: 'none', background: theme.cream }}
                  />
                  <button type="submit" disabled={submitingReview || !reviewRating}
                    style={{ marginTop: 16, padding: '12px 32px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: reviewRating ? 'pointer' : 'not-allowed', fontFamily: theme.font.body }}>
                    {t('rentalDetail.reviewSubmit')}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {/* Cancel button */}
        {rental.status === 'pending_payment' && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button
              disabled={cancelling}
              onClick={async () => {
                // Had no guard of any kind: two taps sent two DELETEs, and the
                // second one runs against a booking that is already gone.
                if (cancellingRef.current) return;
                if (!window.confirm(t('rentalDetail.cancelConfirm'))) return;
                cancellingRef.current = true;
                setCancelling(true);
                try {
                  await api.delete(`/rentals/${id}`);
                  toast.success(t('rentalDetail.cancelSuccess'));
                  navigate('/my-rentals');
                } catch (err) {
                  toast.error(errorText(err, t('rentalDetail.cancelFailed')));
                  cancellingRef.current = false;
                  setCancelling(false);
                }
              }}
              style={{ background: 'none', border: `1px solid ${theme.red}`, color: theme.red, padding: '10px 28px', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: cancelling ? 'wait' : 'pointer', opacity: cancelling ? 0.6 : 1, fontFamily: theme.font.body }}>
              {cancelling ? t('rentalDetail.cancelling') : t('rentalDetail.cancelBtn')}
            </button>
          </div>
        )}

        {/* Self-cancel with full refund — only while paid and not yet collected
            (Terms §10 / §11 withdrawal right). Once the item is collected the
            rental has started and this is gone. */}
        {rental.status === 'paid' && !rental.picked_up_at && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: theme.grey550, marginBottom: 8, fontFamily: theme.font.body }}>
              {t('rentalDetail.selfCancelNote')}
            </p>
            <button
              disabled={cancelling}
              onClick={async () => {
                if (cancellingRef.current) return;
                if (!window.confirm(t('rentalDetail.selfCancelConfirm'))) return;
                cancellingRef.current = true;
                setCancelling(true);
                try {
                  await api.post(`/rentals/${id}/self-cancel`);
                  toast.success(t('rentalDetail.selfCancelSuccess'));
                  navigate('/my-rentals');
                } catch (err) {
                  toast.error(errorText(err, t('rentalDetail.selfCancelFailed')));
                  cancellingRef.current = false;
                  setCancelling(false);
                }
              }}
              style={{ background: 'none', border: `1px solid ${theme.red}`, color: theme.red, padding: '10px 28px', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: cancelling ? 'wait' : 'pointer', opacity: cancelling ? 0.6 : 1, fontFamily: theme.font.body }}>
              {cancelling ? t('rentalDetail.selfCancelling') : t('rentalDetail.selfCancelBtn')}
            </button>
          </div>
        )}

        {/* Withdrawal after collection (Terms §10.iv–v, CRD Art. 9 / 14(3)).
            The right does not end at the locker door — it runs 14 days from the
            booking — so while the rental is live and the window is open the
            customer gets a route to it instead of a "contact us". The deposit is
            deliberately not part of this: it comes back on return, as always. */}
        {['picked_up', 'late'].includes(rental.status) && !rental.withdrawn_at && withdrawalOpen && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: theme.grey550, marginBottom: 8, fontFamily: theme.font.body, lineHeight: 1.7 }}>
              {t('rentalDetail.withdrawNote', { deadline: safeFormat(withdrawalDeadline, 'd MMM yyyy', dateLocale) })}
            </p>
            <button
              disabled={cancelling}
              onClick={async () => {
                if (cancellingRef.current) return;
                if (!window.confirm(t('rentalDetail.withdrawConfirm'))) return;
                cancellingRef.current = true;
                setCancelling(true);
                try {
                  await api.post(`/rentals/${id}/withdraw`);
                  toast.success(t('rentalDetail.withdrawSuccess'));
                  // Stay on the page: the refund figure and the new return date
                  // are the two things the customer needs to read next.
                  setRental(await api.get(`/rentals/${id}`));
                } catch (err) {
                  toast.error(errorText(err, t('rentalDetail.withdrawFailed')));
                } finally {
                  cancellingRef.current = false;
                  setCancelling(false);
                }
              }}
              style={{ background: 'none', border: `1px solid ${theme.red}`, color: theme.red, padding: '10px 28px', borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', cursor: cancelling ? 'wait' : 'pointer', opacity: cancelling ? 0.6 : 1, fontFamily: theme.font.body }}>
              {cancelling ? t('rentalDetail.withdrawing') : t('rentalDetail.withdrawBtn')}
            </button>
          </div>
        )}

        {/* Already withdrawn: show the calculation rather than leave a partial
            refund looking like an unexplained deduction. */}
        {rental.withdrawn_at && (
          <div style={{ background: theme.redWashPale, border: `1px solid ${theme.redBorderPeach}`, borderRadius: 4, padding: 20, marginTop: 24 }}>
            <p style={{ fontSize: 11, letterSpacing: '0.12em', color: theme.redBrick, marginBottom: 8, fontFamily: theme.font.body, textTransform: 'uppercase' }}>
              {t('rentalDetail.withdrawnLabel')}
            </p>
            <p style={{ fontSize: 14, color: theme.ink600, margin: 0, fontFamily: theme.font.body, lineHeight: 1.7 }}>
              {rental.withdrawal_days_charged > 0
                ? t('rentalDetail.withdrawnSummary', {
                  date: safeFormat(rental.withdrawn_at, 'd MMM yyyy', dateLocale),
                  charged: rental.withdrawal_days_charged,
                  total: rental.withdrawal_days_total,
                })
                : t('rentalDetail.withdrawnNoCost', { date: safeFormat(rental.withdrawn_at, 'd MMM yyyy', dateLocale) })}
              {['picked_up', 'late'].includes(rental.status) && rental.due_at
                ? ` ${t('rentalDetail.withdrawReturnBy', { date: safeFormat(rental.due_at, 'd MMM yyyy HH:mm', dateLocale) })}`
                : ''}
            </p>
          </div>
        )}

        {/* How to collect */}
        {rental.status === 'paid' && (
          <div style={{ background: theme.creamDeep, border: `1px solid ${theme.sandDeep}`, borderRadius: 4, padding: 24, marginTop: 24 }}>
            <h3 style={{ fontFamily: theme.font.display, fontSize: 20, marginBottom: 16, color: theme.goldInk }}>{t('rentalDetail.howToCollect')}</h3>
            {[
              t('rentalDetail.collectStep1'),
              t('rentalDetail.collectStep2'),
              t('rentalDetail.collectStep3'),
              t('rentalDetail.collectStep4'),
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: theme.gold, color: theme.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>{i + 1}</div>
                <span style={{ color: theme.goldMoss, fontSize: 14 }}>{step}</span>
              </div>
            ))}
          </div>
        )}
      </Reveal>

      {/* Return Modal */}
      {showReturnModal && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.6), zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}>
          {/* Bottom sheet: safe-bottom keeps the action buttons clear of the
              gesture-navigation bar under Android 15 edge-to-edge. */}
          <div className="safe-bottom" style={{ background: theme.white, borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 600, padding: 32, maxHeight: '80vh', overflow: 'auto' }}>

            {returnResult ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <CheckCircle size={56} color={theme.green} style={{ marginBottom: 20 }} />
                <h2 style={{ fontFamily: theme.font.display, fontSize: 30, marginBottom: 8 }}>{t('rentalDetail.returnCodeTitle')}</h2>
                <div style={{ background: theme.creamDeep, borderRadius: 8, padding: 20, margin: '20px 0', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: theme.grey400, letterSpacing: '0.08em', marginBottom: 6 }}>{t('rentalDetail.returnCode')}</div>
                  <div style={{ fontSize: 40, letterSpacing: '0.25em', fontWeight: 700, color: theme.ink }}>{returnResult.save_code}</div>
                  {returnResult.box_name && (
                    <div style={{ fontSize: 13, color: theme.grey500, marginTop: 8 }}>{t('rentalDetail.boxLabel')}: <strong>{returnResult.box_name}</strong></div>
                  )}
                  <div style={{ fontSize: 14, color: theme.grey600, marginTop: 12 }}>{returnResult.message || t('rentalDetail.returnCodeInstructions')}</div>
                </div>
                <button onClick={() => setShowReturnModal(false)} style={{ width: '100%', padding: '16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.1em', cursor: 'pointer', fontFamily: theme.font.body }}>
                  {t('rentalDetail.returnCodeDone')}
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2 style={{ fontFamily: theme.font.display, fontSize: 28, margin: 0 }}>{t('rentalDetail.modalTitle')}</h2>
                  <button onClick={() => setShowReturnModal(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: theme.grey400 }}>×</button>
                </div>
                <p style={{ color: theme.grey500, fontSize: 14, marginBottom: 24 }}>{t('rentalDetail.modalSubtitle')}</p>

                {loadingMachines ? (
                  <div style={{ textAlign: 'center', padding: 40, color: theme.gold }}>{t('rentalDetail.modalLoading')}</div>
                ) : machines.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: theme.grey400, fontSize: 14 }}>{t('rentalDetail.modalNoMachines')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {machines.map(m => (
                      // No free-slot count any more: the Bestwond cabinet allocates a box
                      // itself when the return order is registered, so the old per-slot
                      // availability was both meaningless and — when it read 0 — silently
                      // disabled returns on a perfectly working machine.
                      <button key={m.id} onClick={() => handleReturn(m.id)} disabled={returning}
                        style={{ padding: '20px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: returning ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: returning ? 0.6 : 1 }}>
                        <div style={{ fontFamily: theme.font.display, fontSize: 20, color: theme.ink, marginBottom: 4 }}>{m.name}</div>
                        <div style={{ fontSize: 13, color: theme.grey400, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={12} /> {m.address || '—'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* In-app document viewer. Used when no new window could be opened — the
          Android WebView refuses window.open, and an <a download> on a blob:
          URL does nothing there at all, so the previous "saved to your device"
          toast was reporting a file that was never written. This shows the
          actual receipt. */}
      {docView && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.75), zIndex: 1100, display: 'flex', flexDirection: 'column' }}>
          <div className="safe-bottom" style={{ background: theme.white, margin: 'auto', width: '100%', maxWidth: 760, height: '92vh', display: 'flex', flexDirection: 'column', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: `1px solid ${theme.sand}` }}>
              <span style={{ fontFamily: theme.font.mono, fontSize: 13, color: theme.grey600 }}>{docView.title}</span>
              <button onClick={() => setDocView(null)}
                style={{ background: 'none', border: `1px solid ${theme.sand}`, color: theme.grey600, padding: '8px 18px', borderRadius: 2, fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer', fontFamily: theme.font.body }}>
                {t('rentalDetail.document.close')}
              </button>
            </div>
            <iframe
              title={docView.title || 'document'}
              srcDoc={docView.html}
              style={{ flex: 1, border: 'none', width: '100%', background: theme.white }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
